<template>
  <div class="flex justify-between items-center w-full">
    <div class="flex flex-col w-full max-w-[calc(100%-18px)] py-1" :style="{ letterSpacing: '0px' }">
      <div class="font-bold">{{ option.label }}</div>
      <div>
        <template v-if="!isBlank">
          <div
            v-if="tags.length"
            class="flex flex-wrap items-center gap-1 font-size-3"
            :style="{ color: themeVars.text2 }"
          >
            <div class="font-bold">{{ t('canvasUI.CCreateCanvasModal.tags') }}</div>
            <div v-for="(tag, index) in tags" :key="index" class="truncate">
              {{ tag }}<span v-if="index < tags.length - 1">, </span>
            </div>
            <CPopover
              v-if="remainingTags.length"
              :keep-alive-on-hover="false"
              :placement="option.index === 0 ? 'bottom' : 'top'"
              :to="'body'"
              trigger="hover"
            >
              <template #trigger>
                <div
                  class="underline underline-solid cursor-pointer select-none c-create-canvas-model-templates-select-option__more-tags"
                >
                  {{
                    t('canvasUI.components.tables.andCountMore', {
                      count: remainingTags.length,
                    })
                  }}
                </div>
              </template>

              <div v-for="(tag, index) in remainingTags" :key="index" class="flex flex-col gap-1">
                {{ tag }}
              </div>
            </CPopover>
          </div>
          <div v-else class="font-size-3" :style="{ color: themeVars.text2 }">
            {{ t('canvasUI.components.tables.noTags') }}
          </div>
          <div
            v-if="showLastModified && formattedModifiedAt"
            class="font-size-3 mt-1"
            :style="{ color: themeVars.text2 }"
          >
            {{ t('canvasUI.CCreateCanvasModal.lastModifiedAt') }} {{ formattedModifiedAt }}
          </div>
        </template>
      </div>
    </div>
    <div v-if="!isBlank && !hideOpenTemplate" class="flex-shrink-0">
      <CIcon
        :color="themeVars.primary"
        :icon="platformType === PLATFORM_TYPE.web ? 'arrow-up-right-from-square' : 'eye'"
        size="18px"
        @click.stop="emit('preview')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { type SelectOption } from 'naive-ui'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CPopover from '@lib/components/CPopover/CPopover.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { PLATFORM_TYPE } from '@lib/constants/cdp.const'
import { HIDE_TAGS_WITH_PREFIX } from '@lib/constants/conventions.const'
import { formatDateTime } from '@lib/util/date'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    option: SelectOption & { tags?: string[]; modifiedAt?: string; index: number }
    platformType?: (typeof PLATFORM_TYPE)[keyof typeof PLATFORM_TYPE]
    maxTagsToShow?: number
    isBlank?: boolean
    hideOpenTemplate?: boolean
    showLastModified?: boolean
  }>(),
  {
    maxTagsToShow: 5,
    showLastModified: false,
  },
)

const emit = defineEmits<{ preview: [] }>()

const { t } = useI18n()
const themeVars = useThemeVars()

const tags = computed(
  () =>
    props.option.tags
      ?.slice?.(0, props.maxTagsToShow)
      ?.filter?.((t) => !HIDE_TAGS_WITH_PREFIX.some((prefix) => t.startsWith(prefix))) ?? [],
)
const remainingTags = computed(
  () =>
    props.option.tags
      ?.slice?.(props.maxTagsToShow)
      ?.filter?.((t) => !HIDE_TAGS_WITH_PREFIX.some((prefix) => t.startsWith(prefix))) ?? [],
)

const formattedModifiedAt = computed(() => {
  if (!props.showLastModified) return null
  return props.option.modifiedAt ? formatDateTime(props.option.modifiedAt) : null
})
</script>
